import {
  ChakraBox,
  ChakraFlex,
  ChakraHStack,
  FAIconName,
  FireflyButton,
  FireflyIconButton,
  InputGroupV2,
  ModalV2,
  TextInputV2,
} from '@equip.health/ui';
import { useRef, useState } from 'react';
import { useUpdateQoLGoalMutation } from '~/store/services/carePlan';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { useFocusInputOnMount } from '~/lib/hooks/useFocusInputOnMount';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import { CarePlanQolGoalResponse } from '~/lib/types/carePlan/carePlans';

const TOP_LEVEL_ID = 'edit-qol-goal-modal';

interface EditQoLGoalModalProps {
  qolGoal: CarePlanQolGoalResponse;
  treatmentEpisodeId: string;
  isInitialFocusDescription?: boolean;
  onClose: () => void;
}

const EditQoLGoalModal = ({
  qolGoal,
  treatmentEpisodeId,
  isInitialFocusDescription = false,
  onClose,
}: EditQoLGoalModalProps) => {
  const [title, setTitle] = useState(qolGoal.title ?? '');
  const [isShowDescription, setIsShowDescription] = useState(
    !!qolGoal.description || isInitialFocusDescription,
  );
  const [description, setDescription] = useState(qolGoal.description ?? '');
  const descriptionInputRef = useRef<HTMLDivElement | null>(null);

  const { showErrorToast } = useErrorToast();

  const [updateQoLGoal, { isLoading: isUpdateGQoLGoalLoading }] =
    useUpdateQoLGoalMutation();

  useFocusInputOnMount(
    () =>
      descriptionInputRef.current?.querySelector<
        HTMLInputElement | HTMLTextAreaElement
      >('input:not([type="hidden"]), textarea') ?? null,
    isInitialFocusDescription,
  );

  const handleSave = async () => {
    try {
      await updateQoLGoal({
        goalId: qolGoal.id,
        treatmentEpisodeId,
        body: {
          description,
          statusCode: qolGoal.status.code,
          title,
        },
      }).unwrap();
    } catch (error) {
      showErrorToast(GlobalErrorCode.UPDATE_QOL_GOAL, error.status, error);
    }
    onClose();
  };

  const handleRemoveDescription = () => {
    setIsShowDescription(false);
    setDescription('');
  };

  return (
    <ModalV2
      bodyOverflow="visible"
      footerContent={
        <ChakraHStack justifyContent="space-between" width="100%">
          <FireflyButton
            id={`${TOP_LEVEL_ID}__cancel`}
            isLoading={isUpdateGQoLGoalLoading}
            leftIcon={FAIconName.timesRegular}
            onClick={onClose}
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__save`}
            isDisabled={!title}
            isLoading={isUpdateGQoLGoalLoading}
            leftIcon={FAIconName.checkRegular}
            onClick={handleSave}
          >
            Save changes
          </FireflyButton>
        </ChakraHStack>
      }
      isOpen
      onClose={onClose}
      size="4xl"
      title="Edit quality of life goal"
    >
      <ChakraBox
        border="1px"
        borderColor="neutral.line"
        borderRadius="12px"
        marginBottom="24px"
        paddingX="18px"
        paddingY="12px"
      >
        <ChakraBox marginBottom="12px">
          <InputGroupV2 id={`${TOP_LEVEL_ID}_title-input-group`}>
            <TextInputV2
              id={`${TOP_LEVEL_ID}_title-input`}
              onChange={setTitle}
              placeholder="Please provide a title for this goal"
              value={title}
            />
          </InputGroupV2>
        </ChakraBox>
        {!isShowDescription && (
          <FireflyButton
            id={`${TOP_LEVEL_ID}_add-desc-btn`}
            leftIcon={FAIconName.plusRegular}
            marginBottom="6px"
            marginTop="8px"
            onClick={() => setIsShowDescription(true)}
            variant="tertiary"
          >
            Add goal description
          </FireflyButton>
        )}
        {isShowDescription && (
          <ChakraFlex alignItems="center" gap="12px" ref={descriptionInputRef}>
            <InputGroupV2 id={`${TOP_LEVEL_ID}_desc-input-group`}>
              <TextInputV2
                id={`${TOP_LEVEL_ID}_desc-input`}
                onChange={setDescription}
                placeholder="Please provide a description for this goal"
                value={description}
              />
            </InputGroupV2>
            <ChakraBox>
              <FireflyIconButton
                color="critical.base"
                icon={FAIconName.trashRegular}
                id={`${TOP_LEVEL_ID}_delete-desc-btn`}
                onClick={handleRemoveDescription}
                variant="tertiary"
              />
            </ChakraBox>
          </ChakraFlex>
        )}
      </ChakraBox>
    </ModalV2>
  );
};

export default EditQoLGoalModal;
